import type { User } from 'firebase/auth';
// TODO: EXCH-19585 - these documents are temporarily kept here instead of being generated from the OXS GraphQL
// schema; they will be moved as part of https://openxtechinc.atlassian.net/browse/EXCH-19585
// The mutation is the same as oa-ui's SetTermsAndConditionsAccepted.gql.
import TERMS_QUERY from '../../../graphql/oxs/OxsTerms.gql?raw';
import ACCEPT_TERMS_MUTATION from '../../../graphql/oxs/SetTermsAndConditionsAccepted.gql?raw';

// Proxied by the login-app nginx to the internal OA GraphQL load balancer.
const OXS_GRAPHQL_PATH = '/oxs/graphql';
// OA sets this claim only for users that have an OXS (Hasura `user`) row.
const HASURA_CLAIM = 'https://hasura.io/jwt/claims';

export type OxsTerms = {
  userId: string;
  accepted: boolean;
};

type OxsTermsQueryData = { user?: { id: string; terms_and_conditions_accepted: boolean | null }[] };
type OxsTermsQueryVariables = { uid: string };

type SetTermsAndConditionsAcceptedData = { update_user_by_pk: { terms_and_conditions_accepted: boolean } | null };
type SetTermsAndConditionsAcceptedVariables = { id: string; accepted: boolean; date: string };

type GraphqlResponse<TData> = {
  data?: TData;
  errors?: { message: string }[];
};

const postOxsGraphql = async <TData, TVariables>(
  currentUser: User,
  query: string,
  variables: TVariables,
): Promise<TData> => {
  const idToken = await currentUser.getIdToken();
  const response = await fetch(OXS_GRAPHQL_PATH, {
    body: JSON.stringify({ query, variables }),
    headers: {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    method: 'POST',
  });

  if (!response.ok) {
    throw new Error(`OXS GraphQL request failed ${response.status}:${response.statusText}`);
  }

  const { data, errors }: GraphqlResponse<TData> = await response.json();

  if (errors?.length || !data) {
    throw new Error(errors?.[0]?.message ?? 'OXS GraphQL returned no data');
  }

  return data;
};

/**
 * Resolves the OXS T&C state of the current user, or `null` when they are not an OXS user.
 */
export const fetchOxsTerms = async (currentUser: User): Promise<OxsTerms | null> => {
  const { claims } = await currentUser.getIdTokenResult();

  if (!claims[HASURA_CLAIM]) {
    return null;
  }

  const { user } = await postOxsGraphql<OxsTermsQueryData, OxsTermsQueryVariables>(currentUser, TERMS_QUERY, {
    uid: currentUser.uid,
  });

  if (!Array.isArray(user)) {
    throw new Error('OXS GraphQL response has no user list');
  }

  // The claim says this is an OXS user, so a missing row is an error rather than a reason to skip the T&C.
  if (!user.length) {
    throw new Error('OXS user not found');
  }

  return { userId: user[0].id, accepted: user[0].terms_and_conditions_accepted === true };
};

export const acceptOxsTerms = async (currentUser: User, userId: string): Promise<void> => {
  const { update_user_by_pk } = await postOxsGraphql<
    SetTermsAndConditionsAcceptedData,
    SetTermsAndConditionsAcceptedVariables
  >(currentUser, ACCEPT_TERMS_MUTATION, { id: userId, accepted: true, date: new Date().toISOString() });

  // Hasura returns null instead of an error when the row is missing or not updatable by the caller.
  if (!update_user_by_pk?.terms_and_conditions_accepted) {
    throw new Error('OXS T&C acceptance was not saved');
  }
};
